import { FontDescription, LeadingFactor, normalizedFontFamily, scaledBearing,
         Text, TextAlignment, TextArea, TextChunk, TextChunkKind, TextColor,
         TextSize
       } from './text.model';

export function applyTextColor(node: HTMLDivElement, color: TextColor): void
{
  node.style.color = color;
}

export function applyLeading(
  node: HTMLDivElement, leading: LeadingFactor, fontSize: TextSize): void
{
  node.style.lineHeight = `${leading}`;
}

export function applyFontFamily(node: HTMLDivElement, family: string): void
{
  node.style.fontFamily = normalizedFontFamily(family);
}

export function applyAlignmentToNode(
  node: HTMLDivElement,
  horizontal: TextAlignment,
  vertical: TextAlignment,
  fontSize: TextSize,
  leading: LeadingFactor,
  area: TextArea
): void
{
  const verticalOffset = (alignment: TextAlignment): number => {
    const lineHeight = fontSize * leading;
    const textHeight = node.childNodes.length * lineHeight;

    switch (alignment) {
      case TextAlignment.Start: return 0;
      case TextAlignment.Middle: return (area.height - textHeight) / 2;
      case TextAlignment.End: return area.height - textHeight;
      default: return 0; // not reached
    }
  };

  const textAlignment = (alignment: TextAlignment): string => {
    switch (alignment) {
      case TextAlignment.Start: return 'left';
      case TextAlignment.Middle: return 'center';
      case TextAlignment.End: return 'right';
      default: return ''; // not reached
    }
  };

  const offset = verticalOffset(vertical);
  node.style.paddingTop = `${offset}px`;
  node.style.height = `${area.height - offset}px`;

  node.style.textAlign = textAlignment(horizontal);
}

export function applyFontSizeToNode(node: HTMLDivElement, size: TextSize): void
{
  node.style.fontSize = `${size}px`;
}

export function applyChunksToNode(
  node: HTMLDivElement, chunks: Array<TextChunk>): void
{
  interface SelectionPosition {
    paragraph: HTMLParagraphElement;
    offset: number;
  }

  const tryFindTextNode = (paragraph: HTMLParagraphElement) => (
    [].slice
      .apply(paragraph.childNodes)
      .filter(c => c.nodeName === '#text')[0] || paragraph
  );

  const renderContent = (chunk: TextChunk): string => {
    return chunk.kind === TextChunkKind.Control ? '' : (chunk.content || '');
  };

  const makeParagraph = () =>
    node.ownerDocument.createElement('P') as HTMLParagraphElement;

  let currentParagraph = makeParagraph();
  let selectionPositions: Array<SelectionPosition> = [];

  const pushParagraph = () => {
    if (currentParagraph !== undefined) {
      currentParagraph.appendChild(node.ownerDocument.createElement('BR'));
      node.appendChild(currentParagraph);
      currentParagraph = undefined;
    }
  };

  const addContent = (chunk: TextChunk) => {
    if (currentParagraph === undefined) {
      currentParagraph = makeParagraph();
    }

    currentParagraph.textContent += renderContent(chunk);
  };

  const addSelectionPosition =
    (paragraph: HTMLParagraphElement, offset: number) => {
      selectionPositions.push({
        paragraph: paragraph,
        offset: offset
      });
    };

  node.innerHTML = '';

  for (const chunk of chunks) {
    if (chunk.breaksLine) {
      pushParagraph();
    }

    addContent(chunk);

    if (chunk.kind === TextChunkKind.Control) {
      if (chunk.selection !== undefined) {
        addSelectionPosition(
          currentParagraph,
          currentParagraph.textContent.length);
      }
    }
  }

  pushParagraph();

  if (selectionPositions.length > 0) {
    const start = [
      tryFindTextNode(selectionPositions[0].paragraph),
      selectionPositions[0].offset
    ];

    const end = selectionPositions.length > 1
      ? [tryFindTextNode(selectionPositions[1].paragraph),
         selectionPositions[1].offset
        ]
      : start;

    const selection = window.getSelection();
    selection.removeAllRanges();

    const range = node.ownerDocument.createRange();
    range.setStart(start[0], start[1]);
    range.setEnd(end[0], end[1]);
    selection.addRange(range);
  }
}

export function applyBearing(
  node: HTMLDivElement, model: Text,
  font: FontDescription,
  area: TextArea): void
{
  const absoluteBearing = scaledBearing(font, model);
  const margin = absoluteBearing.left + absoluteBearing.right;

  node.style.paddingLeft = `${absoluteBearing.left}px`;
  node.style.paddingRight = `${absoluteBearing.right}px`;
  node.style.width = `${area.width - margin}px`;
}



// WEBPACK FOOTER //
// ./src/app/text/text.view.div.renderer.ts